import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { clearAuthData, isJwtAuthFailure } from "../../components/content/axiosConfig";
import Modal from "react-modal";
import { enqueueSnackbar } from "notistack";
import Swal from 'sweetalert2';
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const WeightUnits = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalData, setModalData] = useState({ Name: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    const fetchWeightUnitsPage = useCallback(async ({ search, page, limit }) => {
        try {
            return await axiosInstance.get(`/api/WeightUnit/GetAll`, {
                params: { search, page, limit },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return { data: { data: [], total: 0 } };
            }
            const errorMessage = error.response?.data?.message || "An error occurred.";
            enqueueSnackbar(errorMessage, {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
                autoHideDuration: 3000,
            });
            throw error;
        }
    }, []);

    const {
        rows: fishTypes,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
        refetch,
    } = useServerPaginatedList(fetchWeightUnitsPage);

    const DeleteUser = async (unitId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                setIsSubmitting(true);
                await axiosInstance.delete(`/api/WeightUnit/Delete/${unitId}`);
                showSuccessToast("Weight unit deleted successfully");
                await refetch();
            } catch (error) {
                if (isJwtAuthFailure(error)) {
                    clearAuthData();
                    window.location.href = "/user/login";
                    return;
                }
                showErrorToast(error.response?.data?.message || "An error occurred.");
                console.error("Error deleting weight unit:", error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const openModal = (data = null) => {
        if (data) {
            setIsEditing(true);
            setEditId(data.id);
            setModalData({ Name: data.Name });
        } else {
            setIsEditing(false);
            setEditId(null);
            setModalData({ Name: "" });
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleChange = (e) => {
        const { id, name, value, type, checked } = e.target;
        if (type === "radio") {
            setModalData({
                ...modalData,
                [name]: value === "true",
            });
        } else {
            setModalData({
                ...modalData,
                [id]: type === "checkbox" ? checked : value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!String(modalData.Name || "").trim()) {
            showErrorToast("Name is required");
            return;
        }
        try {
            setIsSubmitting(true);
            if (isEditing) {
                await axiosInstance.put(`/api/WeightUnit/Update/${editId}`, modalData);
                showSuccessToast("Weight unit updated successfully");
            } else {
                await axiosInstance.post(`/api/WeightUnit/Add`, modalData);
                showSuccessToast("Weight unit added successfully");
            }
            await refetch();
            closeModal();
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                return;
            }
            showErrorToast(error.response?.data?.message || "An error occurred.");
            console.error("Error saving weight unit:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading && fishTypes.length === 0) {
        return <Preloader />;
    }

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ul className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/">Home</Link>
                        </li>
                        <li className="breadcrumb-item active">
                           WeightUnits
                        </li>
                    </ul>
                    <h1 className="page-header mb-0">Weight Unit</h1>
                </div>
                <div className="ms-auto">
                    <button
                        type="button"
                        className="btn btn-success btn-rounded px-4 rounded-pill"
                        onClick={() => openModal()}
                        aria-label="Add weight unit"
                        data-testid="add-weight-unit"
                    >
                        <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true"></i>
                        Add
                    </button>
                </div>
            </div>
            <div className="card border-0">
                <div className="tab-content p-3">
                    <div className="tab-pane fade show active" id="allTab">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Weight Unit..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="table-responsive mb-3">
                            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={2} className="text-center text-muted py-5">
                                                Loading…
                                            </td>
                                        </tr>
                                    ) : (
                                        fishTypes.map((web) => (
                                            <tr key={web.id}>
                                                <td>{web.Name}</td>
                                                <td>
                                                    <span onClick={() => openModal(web)}>
                                                        <i className="fa fa-pencil fs-13px fa-fw me-5px"></i>
                                                    </span>
                                                    <span>
                                                        <i
                                                            onClick={() => DeleteUser(web.id)}
                                                            className="fa fa-trash fs-13px fa-fw me-5px"
                                                        ></i>
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <ListPagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            entriesPerPage={entriesPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                style={{
                    content: {
                        width: "800px",
                        height: "400px",
                        margin: "auto",
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -20%)",
                        padding: "50px",
                    },
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <form onSubmit={handleSubmit} className="fs-13px">
                    <div className="d-flex align-items-center mb-3">
                        <div>
                            <h2>{isEditing ? "Edit Weight Unit" : "Add Weight Unit"}</h2>
                        </div>
                        <div className="ms-auto">
                            <button
                                type="submit"
                                className="btn btn-success btn-rounded px-4 rounded-pill"
                                disabled={isSubmitting}
                            >
                                <i className="fa fa-check fa-lg me-2 ms-n2 text-success-900"></i>
                                Submit
                            </button>
                        </div>
                    </div>
                    <div className="card border-0">
                        <div className="tab-content p-3">
                            <div className="tab-pane fade show active" id="allTab">
                                <div className="form-floating mb-15px">
                                    <input
                                        type="text"
                                        className="form-control h-45px fs-13px"
                                        placeholder="Name"
                                        id="Name"
                                        name="Name"
                                        value={modalData.Name}
                                        onChange={handleChange}
                                    />
                                    <label
                                        htmlFor="Name"
                                        className="d-flex align-items-center fs-13px text-gray-600"
                                    >
                                        Name <span>*</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default WeightUnits;
